import React from 'react'
import { Avatar, AvatarImage, AvatarFallback } from '../../../Avatar'
import { Button, type ButtonProps } from '../../../Button'
import { cn, generateInitials } from '../../../../../lib/utils'
import { Chip, type ChipProps } from '../../../Chip'
import Link from 'next/link'

import type { AvatarProps } from '../../../Avatar'

export interface ChatTopbarProps {
  title?: string
  avatar: AvatarProps
  href?: string
  id?: string
  activeTime?: string
  actions?: ButtonProps[]
  view?: 'open' | 'collapsed' | 'hidden'
  isLoading?: boolean
  chips?: Omit<ChipProps, 'className'>[]
  handleViewChange?: (view: 'open' | 'collapsed' | 'hidden') => void
  className?: string
}

export default function ChatTopbar({
  title,
  id,
  href,
  activeTime,
  avatar,
  actions,
  view,
  isLoading,
  chips,
  className,
  handleViewChange,
}: ChatTopbarProps) {
  const isCollapsed = view === 'collapsed'
  const isHidden = view === 'hidden'

  return (
    <div
      className={cn(
        'h-14 rounded-lg flex items-center px-2 flex-row gap-3 w-full transition-colors duration-200 cursor-pointer',
        'hover:bg-muted/70 focus:bg-muted/70 focus:outline-none',
        {
          'bg-accent hover:bg-accent/90 h-10': isCollapsed,
        },
        className,
      )}
      title={`${title}-header-${id}`}
      onClick={() => {
        isCollapsed && handleViewChange && handleViewChange('open')
      }}
      role="button"
    >
      <Avatar
        className={cn(
          'flex h-8 w-8 rounded-full text-xs border overflow-hidden shadow-sm',
          {
            'bg-accent ring-2 ring-accent/20': isCollapsed,
          },
        )}
      >
        {isLoading ? (
          <div className="w-full h-full animate-pulse bg-accent rounded-lg" />
        ) : (
          <>
            <AvatarImage src={avatar.img} seed={avatar.seed} />
            <AvatarFallback>
              {generateInitials(avatar.name || '')}
            </AvatarFallback>
          </>
        )}
      </Avatar>
      <div
        className={cn(
          'flex flex-col justify-center text-start overflow-hidden w-full gap-0.5',
          {
            'text-primary': !isCollapsed,
          },
        )}
      >
        <div className="flex flex-col gap-0.5 overflow-hidden">
          {isLoading ? (
            <div className="w-28 h-4 animate-pulse bg-accent rounded-lg" />
          ) : (
            <>
              <div className="w-fit font-normal text-xs truncate border-b border-dotted border-primary/40 text-primary/90 hover:text-primary transition-colors duration-200">
                {href ? (
                  <Link href={href} className="hover:underline">
                    {avatar.name}
                  </Link>
                ) : (
                  avatar.name
                )}
              </div>
              {!isCollapsed && activeTime && (
                <div className="text-[10px] text-muted-foreground/60">
                  {activeTime}
                </div>
              )}
            </>
          )}
        </div>
        {!isCollapsed && chips && chips.length > 0 && (
          <div className="flex items-center gap-1">
            {chips.map((chip, index) => (
              <Chip key={index} {...chip} />
            ))}
          </div>
        )}
      </div>

      {actions && actions.length > 0 && (
        <div className="flex flex-row items-center gap-1 shrink-0">
          {actions.map((item, id) => (
            <Button
              href={item.href}
              size={item.size}
              key={id}
              title={`${title}-${item.id}`}
              variant={item.variant}
              alert={item.alert}
              className={cn('p-1', item.className)}
              leftIcon={item.leftIcon}
              handleClick={(event) => {
                event.stopPropagation()
                item.handleClick && item.handleClick(event)
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}
